import * as React from 'react';
import { Search, ChevronRight, ChevronLeft, ChevronsRight, ChevronsLeft } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Input } from './input';
import { Button } from './button';
import { ScrollArea } from './scroll-area';

export interface DualListBoxProps<T> {
  /** All available items that can be selected */
  available: T[];
  /** Currently selected item IDs */
  selected: string[];
  /** Callback when selection changes */
  onSelectionChange: (selectedIds: string[]) => void;
  /** Function to get the unique ID of an item */
  getItemId: (item: T) => string;
  /** Function to get the display label of an item */
  getItemLabel: (item: T) => string;
  /** Optional function to get a secondary label (shown below main label) */
  getItemSubLabel?: (item: T) => string;
  /** Label for the available items list */
  availableLabel?: string;
  /** Label for the selected items list */
  selectedLabel?: string;
  /** Placeholder text for the search input */
  searchPlaceholder?: string;
  /** Whether the component is disabled */
  disabled?: boolean;
  /** Custom class name for the container */
  className?: string;
  /** Height of each list box (default: 300px) */
  listHeight?: number;
}

interface ListBoxItemProps {
  label: string;
  subLabel?: string;
  onClick: () => void;
  disabled?: boolean;
  selected?: boolean;
}

function ListBoxItem({ label, subLabel, onClick, disabled, selected }: ListBoxItemProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'w-full text-left px-3 py-2 text-sm transition-colors',
        'hover:bg-accent hover:text-accent-foreground',
        'focus:bg-accent focus:text-accent-foreground focus:outline-none',
        disabled && 'opacity-50 cursor-not-allowed',
        selected && 'bg-accent'
      )}
    >
      <div className="font-medium truncate">{label}</div>
      {subLabel && (
        <div className="text-xs text-muted-foreground truncate">{subLabel}</div>
      )}
    </button>
  );
}

export function DualListBox<T>({
  available,
  selected,
  onSelectionChange,
  getItemId,
  getItemLabel,
  getItemSubLabel,
  availableLabel = 'Available',
  selectedLabel = 'Selected',
  searchPlaceholder = 'Search...',
  disabled = false,
  className,
  listHeight = 300,
}: DualListBoxProps<T>) {
  const [searchTerm, setSearchTerm] = React.useState('');
  const [highlightedAvailable, setHighlightedAvailable] = React.useState<Set<string>>(new Set());
  const [highlightedSelected, setHighlightedSelected] = React.useState<Set<string>>(new Set());

  // Create a set of selected IDs for fast lookup
  const selectedSet = React.useMemo(() => new Set(selected), [selected]);

  // Split items into available and selected lists
  const availableItems = React.useMemo(() => {
    return available.filter((item) => !selectedSet.has(getItemId(item)));
  }, [available, selectedSet, getItemId]);

  const selectedItems = React.useMemo(() => {
    // Preserve the order of selected items as they were added
    return available.filter((item) => selectedSet.has(getItemId(item)));
  }, [available, selectedSet, getItemId]);

  // Filter available items by search term
  const filteredAvailableItems = React.useMemo(() => {
    if (!searchTerm.trim()) return availableItems;
    const term = searchTerm.toLowerCase();
    return availableItems.filter((item) => {
      const label = getItemLabel(item).toLowerCase();
      const subLabel = getItemSubLabel?.(item)?.toLowerCase() || '';
      return label.includes(term) || subLabel.includes(term);
    });
  }, [availableItems, searchTerm, getItemLabel, getItemSubLabel]);

  // Filter selected items by search term
  const filteredSelectedItems = React.useMemo(() => {
    if (!searchTerm.trim()) return selectedItems;
    const term = searchTerm.toLowerCase();
    return selectedItems.filter((item) => {
      const label = getItemLabel(item).toLowerCase();
      const subLabel = getItemSubLabel?.(item)?.toLowerCase() || '';
      return label.includes(term) || subLabel.includes(term);
    });
  }, [selectedItems, searchTerm, getItemLabel, getItemSubLabel]);

  const handleSelectItem = (item: T) => {
    if (disabled) return;
    const id = getItemId(item);
    onSelectionChange([...selected, id]);
    setHighlightedAvailable(new Set());
  };

  const handleDeselectItem = (item: T) => {
    if (disabled) return;
    const id = getItemId(item);
    onSelectionChange(selected.filter((s) => s !== id));
    setHighlightedSelected(new Set());
  };

  const handleMoveHighlightedToSelected = () => {
    if (disabled || highlightedAvailable.size === 0) return;
    const newSelected = [...selected, ...Array.from(highlightedAvailable)];
    onSelectionChange(newSelected);
    setHighlightedAvailable(new Set());
  };

  const handleMoveHighlightedToAvailable = () => {
    if (disabled || highlightedSelected.size === 0) return;
    onSelectionChange(selected.filter((id) => !highlightedSelected.has(id)));
    setHighlightedSelected(new Set());
  };

  const handleSelectAll = () => {
    if (disabled) return;
    const allIds = filteredAvailableItems.map(getItemId);
    onSelectionChange([...selected, ...allIds]);
    setHighlightedAvailable(new Set());
  };

  const handleDeselectAll = () => {
    if (disabled) return;
    const filteredIds = new Set(filteredSelectedItems.map(getItemId));
    onSelectionChange(selected.filter((id) => !filteredIds.has(id)));
    setHighlightedSelected(new Set());
  };

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      {/* Search input */}
      <div className="relative">
        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input
          type="text"
          placeholder={searchPlaceholder}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-8"
          disabled={disabled}
        />
      </div>

      {/* Dual list boxes */}
      <div className="flex gap-2">
        {/* Available items list */}
        <div className="flex-1 border rounded-md">
          <div className="px-3 py-2 border-b bg-muted/50 flex items-center justify-between">
            <span className="text-sm font-medium">
              {availableLabel} ({filteredAvailableItems.length})
            </span>
          </div>
          <ScrollArea style={{ height: listHeight }}>
            <div className="divide-y">
              {filteredAvailableItems.length === 0 ? (
                <div className="px-3 py-8 text-center text-sm text-muted-foreground">
                  No items available
                </div>
              ) : (
                filteredAvailableItems.map((item) => (
                  <ListBoxItem
                    key={getItemId(item)}
                    label={getItemLabel(item)}
                    subLabel={getItemSubLabel?.(item)}
                    onClick={() => handleSelectItem(item)}
                    disabled={disabled}
                    selected={highlightedAvailable.has(getItemId(item))}
                  />
                ))
              )}
            </div>
          </ScrollArea>
        </div>

        {/* Action buttons */}
        <div className="flex flex-col justify-center gap-1">
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={handleSelectAll}
            disabled={disabled || filteredAvailableItems.length === 0}
            title="Add all"
          >
            <ChevronsRight className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={handleMoveHighlightedToSelected}
            disabled={disabled || highlightedAvailable.size === 0}
            title="Add selected"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={handleMoveHighlightedToAvailable}
            disabled={disabled || highlightedSelected.size === 0}
            title="Remove selected"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={handleDeselectAll}
            disabled={disabled || filteredSelectedItems.length === 0}
            title="Remove all"
          >
            <ChevronsLeft className="h-4 w-4" />
          </Button>
        </div>

        {/* Selected items list */}
        <div className="flex-1 border rounded-md">
          <div className="px-3 py-2 border-b bg-muted/50 flex items-center justify-between">
            <span className="text-sm font-medium">
              {selectedLabel} ({filteredSelectedItems.length})
            </span>
          </div>
          <ScrollArea style={{ height: listHeight }}>
            <div className="divide-y">
              {filteredSelectedItems.length === 0 ? (
                <div className="px-3 py-8 text-center text-sm text-muted-foreground">
                  No items selected
                </div>
              ) : (
                filteredSelectedItems.map((item) => (
                  <ListBoxItem
                    key={getItemId(item)}
                    label={getItemLabel(item)}
                    subLabel={getItemSubLabel?.(item)}
                    onClick={() => handleDeselectItem(item)}
                    disabled={disabled}
                    selected={highlightedSelected.has(getItemId(item))}
                  />
                ))
              )}
            </div>
          </ScrollArea>
        </div>
      </div>
    </div>
  );
}

export default DualListBox;
